import { useState } from 'react';
import WriteReviewForm from './WriteReviewForm';

interface BasicReviewsSectionProps {
  rating: number;
  totalReviews: number;
  businessName: string;
}

export default function BasicReviewsSection({
  rating,
  totalReviews,
  businessName,
}: BasicReviewsSectionProps) {
  const [sortOption, setSortOption] = useState<'newest' | 'highest' | 'lowest'>('newest');
  const [sortOpen, setSortOpen] = useState(false);

  const sortOptions = [
    { value: 'newest', label: 'Most Recent' },
    { value: 'highest', label: 'Highest Rating' },
    { value: 'lowest', label: 'Lowest Rating' }
  ];

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
      <h3 className="text-lg font-bold text-gray-900 mb-5 flex items-center gap-2">
        <div className="w-5 h-5 flex items-center justify-center">
          <i className="ri-chat-quote-line text-teal-600"></i>
        </div>
        Reviews
      </h3>

      {/* Rating Summary */}
      <div className="flex items-center gap-5 mb-6 pb-6 border-b border-gray-100">
        <div className="text-center flex-shrink-0">
          <div className="text-4xl font-black text-gray-900">{rating}</div>
          <div className="flex items-center gap-0.5 mt-1 justify-center">
            {Array.from({ length: 5 }).map((_, i) => (
              <i
                key={i}
                className={`text-sm ${
                  i < Math.floor(rating)
                    ? 'ri-star-fill text-amber-400'
                    : i < rating
                    ? 'ri-star-half-fill text-amber-400'
                    : 'ri-star-line text-gray-300'
                }`}
              ></i>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {totalReviews} review{totalReviews !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex-1">
          <p className="text-sm text-gray-600 leading-relaxed">
            This business has <strong>{totalReviews}</strong> ratings from customers. Be the first to share a detailed review and help others in the Roseville community!
          </p>
        </div>
      </div>

      {/* Write a Review */}
      <div className="mb-6 pb-6 border-b border-gray-100">
        <WriteReviewForm businessName={businessName} />
      </div>

      {/* Sort Controls - Ready for when reviews exist */}
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-gray-500">
          0 written reviews
        </p>
        <div className="relative">
          <button
            onClick={() => setSortOpen(!sortOpen)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-sort-desc text-gray-500 text-sm"></i>
            </div>
            {sortOptions.find((o) => o.value === sortOption)?.label}
            <i className={`ri-arrow-down-s-line text-gray-400 text-sm transition-transform ${sortOpen ? 'rotate-180' : ''}`}></i>
          </button>
          {sortOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setSortOpen(false)}></div>
              <div className="absolute right-0 top-full mt-1 w-44 bg-white border border-gray-200 rounded-xl shadow-lg z-20 py-1 overflow-hidden">
                {sortOptions.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => {
                      setSortOption(opt.value as any);
                      setSortOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors cursor-pointer flex items-center gap-2 ${
                      sortOption === opt.value
                        ? 'text-teal-700 bg-teal-50 font-semibold'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`text-sm ${
                        opt.value === 'newest' ? 'ri-time-line' : opt.value === 'highest' ? 'ri-arrow-up-line' : 'ri-arrow-down-line'
                      } ${sortOption === opt.value ? 'text-teal-600' : 'text-gray-400'}`}></i>
                    </div>
                    {opt.label}
                    {sortOption === opt.value && (
                      <i className="ri-check-line text-teal-600 text-sm ml-auto"></i>
                    )}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Empty state */}
      <div className="text-center py-6">
        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <i className="ri-chat-smile-3-line text-gray-400 text-xl"></i>
        </div>
        <p className="text-sm font-semibold text-gray-700 mb-1">No written reviews yet</p>
        <p className="text-xs text-gray-400 leading-relaxed max-w-xs mx-auto">
          Reviews submitted through the form above will appear here after moderation.
        </p>
      </div>

      {/* Review Source Citation */}
      <div className="mt-6 pt-4 border-t border-gray-100">
        <div className="flex items-start gap-2">
          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
            <i className="ri-information-line text-gray-400 text-sm"></i>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-600 mb-1">About Our Reviews</p>
            <p className="text-[10px] text-gray-400 leading-relaxed">
              All reviews are submitted by verified customers through our platform. Reviews undergo moderation to ensure authenticity and compliance with community guidelines. Ratings are calculated from all customer feedback and updated in real-time.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
